import React, { useMemo, useState } from "react";
import { useGetCustomAgentsQuery } from "../../../../app/api/console/customAgentsApiSlice";
import AgentListItem from "./AgentListItem";
import AgentDetail from "./AgentDetail";

/**
 * Hub "Just for you" custom agents panel inside Console Agent Library.
 * Data: Platform V2 agents (kind=custom) + S3 skills via Console Backend.
 */
function JustForYouPanel({ environment }) {
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [websiteFilter, setWebsiteFilter] = useState(""); // "" | "__none__" | website_id

  const { data, isLoading, isError, error, refetch, isFetching } =
    useGetCustomAgentsQuery({ env: environment });

  const websiteOptions = useMemo(() => {
    const map = new Map();
    for (const agent of data?.agents || []) {
      for (const w of agent.websites || []) {
        if (!w?.website_id || map.has(w.website_id)) continue;
        map.set(w.website_id, {
          website_id: w.website_id,
          website_name: w.website_name || w.website_id,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.website_name || "").localeCompare(b.website_name || "")
    );
  }, [data]);

  const agents = useMemo(() => {
    let list = data?.agents || [];
    if (websiteFilter === "__none__") {
      list = list.filter((a) => !(a.websites || []).length);
    } else if (websiteFilter) {
      list = list.filter((a) =>
        (a.websites || []).some((w) => w.website_id === websiteFilter)
      );
    }
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (a) =>
        (a.title || "").toLowerCase().includes(q) ||
        (a.slug || "").toLowerCase().includes(q) ||
        (a.description || "").toLowerCase().includes(q) ||
        (a.websites || []).some(
          (w) =>
            (w.website_name || "").toLowerCase().includes(q) ||
            (w.website_id || "").toLowerCase().includes(q)
        )
    );
  }, [data, searchQuery, websiteFilter]);

  const selectedWebsiteLabel = useMemo(() => {
    if (!websiteFilter) return null;
    if (websiteFilter === "__none__") return "No websites";
    const hit = websiteOptions.find((w) => w.website_id === websiteFilter);
    return hit?.website_name || websiteFilter;
  }, [websiteFilter, websiteOptions]);

  const selectedStillVisible = agents.some((a) => a.slug === selectedSlug);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="relative flex-1 max-w-[280px]">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search custom agents…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-body-xs bg-white rounded-xl border border-border-default focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40 placeholder:text-text-tertiary"
          />
        </div>

        <select
          value={websiteFilter}
          onChange={(e) => setWebsiteFilter(e.target.value)}
          className="px-3 py-2 text-body-xs bg-white rounded-xl border border-border-default focus:outline-none focus:ring-2 focus:ring-accent-purple/20 min-w-[200px] max-w-[280px]"
          title="Filter agents by website"
        >
          <option value="">All websites</option>
          <option value="__none__">No websites bound</option>
          {websiteOptions.map((w) => (
            <option key={w.website_id} value={w.website_id}>
              {w.website_name}
            </option>
          ))}
        </select>

        {websiteFilter && (
          <button
            type="button"
            onClick={() => setWebsiteFilter("")}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-accent-purple/10 text-body-xs text-text-primary hover:bg-accent-purple/15"
            title="Clear website filter"
          >
            <span className="truncate max-w-[180px]">{selectedWebsiteLabel}</span>
            <span aria-hidden className="text-text-tertiary">
              ×
            </span>
          </button>
        )}

        <span className="text-body-xs text-text-tertiary">
          {data?.total != null ? `${agents.length} / ${data.total}` : ""}
          {isFetching ? " · refreshing" : ""}
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          className="px-3 py-2 border border-border-default rounded-xl text-body-xs text-text-secondary hover:bg-surface-neutral"
        >
          Refresh
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden rounded-2xl border border-border-subtle bg-white">
        <div className="w-80 min-w-[288px] border-r border-border-subtle overflow-y-auto scrollbar-thin">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-purple border-t-transparent" />
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-body-sm text-red-600">
              Failed to load custom agents
              <div className="text-caption mt-1 text-text-tertiary">
                {error?.data?.detail || error?.error || "Check Platform admin API"}
              </div>
            </div>
          ) : agents.length === 0 ? (
            <div className="p-6 text-center text-body-sm text-text-tertiary">
              No custom agents found
            </div>
          ) : (
            agents.map((agent) => (
              <AgentListItem
                key={agent.slug}
                agent={agent}
                isSelected={selectedSlug === agent.slug && selectedStillVisible}
                onClick={() => setSelectedSlug(agent.slug)}
              />
            ))
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {selectedSlug && selectedStillVisible ? (
            <AgentDetail env={environment} slug={selectedSlug} />
          ) : (
            <div className="flex items-center justify-center h-full text-body-sm text-text-tertiary">
              Select a Just-for-you agent to view skills, subskills, and frontmatter
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default JustForYouPanel;
